import React, { useState } from "react";
import styles from "./style.module.css";
import { REST, GRAPHQL, ALL_ENDPOINTS } from "../constants";
import UI from "./ui";
import { useSchema } from "../context/SchemaContext";
import { generateMockFromSchema } from "../utils/schemaParser";

const ApiServiceManager = () => {
  const {
    endpoints: schemaEndpoints,
    hasSchema,
    importSchema,
    clearSchema,
    deleteEndpoint,
    updateEndpoint,
    schemaInfo
  } = useSchema();

  const [apiType, setApiType] = useState(REST);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [showTour, setShowTour] = useState(false);

  const closeTour = () => {
    setShowTour(false);
  };

  const openHelp = () => {
    setShowTour(true);
  };

  // Use imported endpoints if available, otherwise use default
  const currentEndpoints = hasSchema ? schemaEndpoints : ALL_ENDPOINTS;

  // Filter endpoints based on API type
  const filteredEndpoints = currentEndpoints.filter((ep) => {
    if (apiType === REST) {
      return !["QUERY", "MUTATION"].includes(ep.method);
    }
    return ["QUERY", "MUTATION"].includes(ep.method) || ep.type === "query" || ep.type === "mutation";
  });

  const handleTestEndpoint = (endpoint) => {
    setSelectedEndpoint(endpoint);
    setModalOpen(true);
  };

  const handleSchemaImported = (parsedSchema) => {
    importSchema(parsedSchema);
    if (parsedSchema.type === "graphql") {
      setApiType(GRAPHQL);
    } else {
      setApiType(REST);
    }
  };

  const generateMockResponse = (endpoint) => {
    if (!endpoint) return null;

    // Generate mock based on endpoint response schema
    if (endpoint.responses?.["200"]?.schema) {
      return generateMockFromSchema(endpoint.responses["200"].schema);
    }

    // Default mock responses based on endpoint type
    if (endpoint.method === "QUERY" || endpoint.type === "query") {
      return {
        data: {
          [endpoint.name]: [
            { id: "1", name: "Sample Item 1", createdAt: new Date().toISOString() },
            { id: "2", name: "Sample Item 2", createdAt: new Date().toISOString() },
          ]
        }
      };
    }

    if (endpoint.method === "MUTATION" || endpoint.type === "mutation") {
      return {
        data: {
          [endpoint.name]: { id: "1", success: true, message: "Operation completed" }
        }
      };
    }

    // REST endpoints
    return {
      status: 200,
      data: [
        { id: 1, name: "Sample Item 1", description: "This is a mock response" },
        { id: 2, name: "Sample Item 2", description: "This is a mock response" },
      ]
    };
  };

  return (
    <section className={styles.pageLayout}>
      {/* Help Modal */}
      {showTour && (
        <div className={styles.tourOverlay} onClick={closeTour}>
          <div className={styles.tourCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.tourHeader}>
              <h3>How to Use</h3>
              <button className={styles.tourCloseBtn} onClick={closeTour}>
                &times;
              </button>
            </div>
            <div className={styles.tourBody}>
              <div className={styles.tourSteps}>
                <div className={styles.tourStep}>
                  <span className={styles.stepNumber}>1</span>
                  <p>Click <strong>Import Schema</strong> to load your OpenAPI or GraphQL schema</p>
                </div>
                <div className={styles.tourStep}>
                  <span className={styles.stepNumber}>2</span>
                  <p>Use the <strong>sample schemas</strong> to quickly try out the features</p>
                </div>
                <div className={styles.tourStep}>
                  <span className={styles.stepNumber}>3</span>
                  <p>Click <strong>Test</strong> on any endpoint to see mock responses</p>
                </div>
                <div className={styles.tourStep}>
                  <span className={styles.stepNumber}>4</span>
                  <p>Switch between <strong>REST</strong> and <strong>GraphQL</strong> tabs</p>
                </div>
              </div>
            </div>
            <div className={styles.tourFooter}>
              <button className={styles.tourButton} onClick={closeTour}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.title}>API Service Manager</h1>
        <button className={styles.helpBtn} onClick={openHelp}>
          Need help?
        </button>
      </header>

      {/* Description */}
      <p className={styles.description}>
        Import your OpenAPI or GraphQL schema to explore endpoints and generate mock responses.
        Perfect for frontend development when the backend isn't ready yet.
      </p>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <UI.Tabs
            tabList={[{ name: REST }, { name: GRAPHQL }]}
            apiType={apiType}
            setApiType={setApiType}
          />
          <div className={styles.toolbarActions}>
            {hasSchema && (
              <button className={styles.clearAllBtn} onClick={clearSchema}>
                Clear All
              </button>
            )}
            <UI.SchemaImport onSchemaImported={handleSchemaImported} />
          </div>
        </div>

        {/* Schema Info Banner */}
        {hasSchema && schemaInfo && (
          <div className={styles.schemaBanner}>
            <div className={styles.schemaDetails}>
              <strong>{schemaInfo.title}</strong>
              <span className={styles.version}>v{schemaInfo.version}</span>
              <span className={styles.count}>{filteredEndpoints.length} {apiType} endpoints</span>
            </div>
          </div>
        )}

        {/* Endpoints Table */}
        <UI.Table
          endpoints={filteredEndpoints}
          onTest={handleTestEndpoint}
          onDelete={deleteEndpoint}
          onEdit={updateEndpoint}
        />
      </div>

      {/* Footer */}
      <footer className={styles.footer}>
        Ideated and developed by <span>Ruhi</span>
      </footer>

      {/* Response Modal */}
      {isModalOpen && selectedEndpoint && (
        <UI.Modal
          endpoint={selectedEndpoint}
          mockData={generateMockResponse(selectedEndpoint)}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
};

export default ApiServiceManager;
